import React, { useState } from "react";
import {
  Phone,
  MapPin,
  Clock,
  Instagram,
  MessageCircle,
  Send,
} from "lucide-react";
import {
  contactInfo,
  navItems,
  WHATSAPP_NUMBER,
  eventTypes,
  products,
  LOGO_URL,
} from "../../mock";
import ColorfulBrand from "./ColorfulBrand";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useToast } from "../../hooks/use-toast";

const socialMap = { Instagram, MessageCircle };

const emptyForm = {
  name: "",
  phone: "",
  date: "",
  time: "",
  place: "",
  eventType: "",
  kids: "",
  toy: "",
  message: "",
};

const Contato = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(emptyForm);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.date || !form.place || !form.eventType || !form.toy) {
      toast({
        title: "Faltam alguns dados",
        description: "Preencha os campos obrigatórios (*) para solicitar o orçamento.",
      });
      return;
    }

    const lines = [
      "Olá! Gostaria de solicitar um orçamento. 🎉",
      "",
      `*Nome:* ${form.name}`,
      `*WhatsApp:* ${form.phone}`,
      `*Data do evento:* ${form.date}${form.time ? ` às ${form.time}` : ""}`,
      `*Local:* ${form.place}`,
      `*Tipo de evento:* ${form.eventType}`,
      form.kids ? `*Quantidade de crianças:* ${form.kids}` : null,
      `*Brinquedo desejado:* ${form.toy}`,
      form.message ? `*Mensagem:* ${form.message}` : null,
    ].filter(Boolean);

    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
      lines.join("\n")
    )}`;
    window.open(url, "_blank");

    toast({
      title: "Abrindo o WhatsApp... 🎉",
      description: "Sua mensagem já está prontinha. É só enviar!",
    });
  };

  const infoRows = [
    { icon: Phone, label: contactInfo.phone },
    { icon: MapPin, label: contactInfo.address },
    { icon: Clock, label: contactInfo.hours },
  ];

  const fieldClass =
    "w-full rounded-xl bg-[#15131f] border border-white/10 px-4 py-3.5 text-white placeholder-gray-500 outline-none focus:border-[#ec4899] transition-colors";
  const labelClass = "block text-sm font-medium text-gray-300 mb-2";

  return (
    <section id="contato" className="relative pt-20 md:pt-28">
      <div className="max-w-7xl mx-auto px-5">
        <div className="text-center">
          <span className="text-sm font-bold tracking-[0.2em] text-[#ec4899] uppercase">
            Contato
          </span>
          <h2 className="mt-4 font-display font-bold text-white text-3xl md:text-5xl leading-tight">
            Peça seu orçamento
          </h2>
          <p className="mt-6 text-gray-400 text-lg max-w-xl mx-auto leading-relaxed">
            Preencha os dados da sua festa e envie direto pelo WhatsApp com a
            mensagem pronta.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Info */}
          <div className="lg:col-span-2 rounded-3xl border border-white/8 bg-[#0f0e17] p-8 md:p-10">
            <h3 className="font-display font-bold text-white text-2xl mb-8">
              Informações
            </h3>
            <div className="space-y-6">
              {infoRows.map((r, i) => {
                const Icon = r.icon;
                return (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#ec4899]/15 flex items-center justify-center shrink-0">
                      <Icon size={22} className="text-[#ec4899]" />
                    </div>
                    <p className="text-gray-300 pt-2.5">{r.label}</p>
                  </div>
                );
              })}
            </div>
            <div className="mt-8 flex gap-3">
              {contactInfo.social.map((s) => {
                const Icon = socialMap[s.icon];
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-300 hover:bg-[#ec4899] hover:text-white hover:border-[#ec4899] transition-all"
                    aria-label={s.label}
                  >
                    <Icon size={22} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="lg:col-span-3 rounded-3xl border border-white/8 bg-[#0f0e17] p-8 md:p-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>
                  Nome <span className="text-[#ec4899]">*</span>
                </label>
                <input
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Seu nome"
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>
                  WhatsApp <span className="text-[#ec4899]">*</span>
                </label>
                <input
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="(00) 00000-0000"
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>
                  Data do evento <span className="text-[#ec4899]">*</span>
                </label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => update("date", e.target.value)}
                  className={`${fieldClass} [color-scheme:dark]`}
                />
              </div>
              <div>
                <label className={labelClass}>Horário</label>
                <input
                  type="time"
                  value={form.time}
                  onChange={(e) => update("time", e.target.value)}
                  className={`${fieldClass} [color-scheme:dark]`}
                />
              </div>
              <div>
                <label className={labelClass}>
                  Local do evento <span className="text-[#ec4899]">*</span>
                </label>
                <input
                  value={form.place}
                  onChange={(e) => update("place", e.target.value)}
                  placeholder="Bairro, salão ou endereço da festa"
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>
                  Tipo de evento <span className="text-[#ec4899]">*</span>
                </label>
                <Select
                  value={form.eventType}
                  onValueChange={(v) => update("eventType", v)}
                >
                  <SelectTrigger className="h-[52px] rounded-xl bg-[#15131f] border-white/10 text-white px-4 focus:ring-[#ec4899]">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#15131f] border-white/10 text-white">
                    {eventTypes.map((t) => (
                      <SelectItem
                        key={t}
                        value={t}
                        className="focus:bg-[#ec4899]/20 focus:text-white"
                      >
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelClass}>Quantidade de crianças</label>
                <input
                  type="number"
                  min="1"
                  value={form.kids}
                  onChange={(e) => update("kids", e.target.value)}
                  placeholder="Ex: 15"
                  className={fieldClass}
                />
              </div>
              <div>
                <label className={labelClass}>
                  Brinquedo desejado <span className="text-[#ec4899]">*</span>
                </label>
                <Select value={form.toy} onValueChange={(v) => update("toy", v)}>
                  <SelectTrigger className="h-[52px] rounded-xl bg-[#15131f] border-white/10 text-white px-4 focus:ring-[#ec4899]">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#15131f] border-white/10 text-white max-h-72">
                    {products.map((p) => (
                      <SelectItem
                        key={p.id}
                        value={p.name}
                        className="focus:bg-[#ec4899]/20 focus:text-white"
                      >
                        {p.name}
                      </SelectItem>
                    ))}
                    <SelectItem
                      value="Ainda não sei / Quero sugestões"
                      className="focus:bg-[#ec4899]/20 focus:text-white"
                    >
                      Ainda não sei / Quero sugestões
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Mensagem</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Conte mais sobre a sua festa (opcional)"
                  className={`${fieldClass} resize-none`}
                />
              </div>
            </div>

            <button
              type="submit"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#22c55e] hover:bg-[#1eb253] text-white font-extrabold text-base py-4 transition-all hover:-translate-y-0.5"
            >
              <Send size={18} /> SOLICITAR ORÇAMENTO
            </button>
            <p className="mt-4 text-center text-sm text-gray-500">
              Ao enviar, o WhatsApp abre com a mensagem pronta. Nada é cobrado
              nesta etapa.
            </p>
          </form>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-20 border-t border-white/8">
        <div className="max-w-7xl mx-auto px-5 py-14">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Brand */}
            <div>
              <a href="#inicio" className="flex items-center gap-3">
                <img
                  src={LOGO_URL}
                  alt="Casagrande Brinquedos"
                  className="h-12 w-auto object-contain"
                />
                <ColorfulBrand className="text-lg" />
              </a>
              <p className="mt-5 text-gray-400 leading-relaxed max-w-xs">
                {contactInfo.tagline}
              </p>
              <div className="mt-6 flex gap-3">
                {contactInfo.social.map((s) => {
                  const Icon = socialMap[s.icon];
                  return (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noreferrer"
                      className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-300 hover:bg-[#ec4899] hover:text-white hover:border-[#ec4899] transition-all"
                      aria-label={s.label}
                    >
                      <Icon size={20} />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Navegação */}
            <div>
              <h4 className="text-sm font-bold tracking-[0.2em] text-[#ec4899] uppercase mb-5">
                Navegação
              </h4>
              <nav className="flex flex-col gap-3">
                {navItems.map((n) => (
                  <a
                    key={n.label}
                    href={n.href}
                    className="text-gray-400 hover:text-white transition-colors w-fit"
                  >
                    {n.label}
                  </a>
                ))}
              </nav>
            </div>

            {/* Contato */}
            <div>
              <h4 className="text-sm font-bold tracking-[0.2em] text-[#ec4899] uppercase mb-5">
                Contato
              </h4>
              <div className="space-y-4">
                <a
                  href={contactInfo.social[0].href}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors"
                >
                  <MessageCircle size={18} className="text-[#22c55e] shrink-0" />
                  {contactInfo.phone}
                </a>
                <div className="flex items-start gap-3 text-gray-300">
                  <MapPin size={18} className="text-[#ec4899] shrink-0 mt-0.5" />
                  {contactInfo.address}
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <Clock size={18} className="text-[#38bdf8] shrink-0" />
                  {contactInfo.hours}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/5 text-center">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} {contactInfo.owner}. Todos os direitos
              reservados.
            </p>
          </div>
        </div>
      </footer>
    </section>
  );
};

export default Contato;
